import React, { useState, useRef, useEffect, useCallback } from "react";
import CustomDropdown from "../forms/CustomDropdown";
import Icon from "./Icon";
import styles from "./EditableField.module.css";

// Values may be arrays (multiselect), so identity comparison isn't enough to
// tell "unchanged" from "changed".
const sameValue = (a, b) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => String(v) === String(b[i]));
  }
  if (a == null && b == null) return true;
  return a === b;
};

/**
 * Multi-value picker. CustomDropdown is single-value, so this is its own
 * popover: toggling options edits a local draft and the whole selection is
 * committed once, when the popover closes.
 */
function MultiSelectEditor({ options, value, disabled, onCommit, onCancel }) {
  const [selection, setSelection] = useState(() => (Array.isArray(value) ? value.map(String) : []));
  const containerRef = useRef(null);
  // Held in a ref so the outside-click listener always commits the latest
  // selection without being torn down and rebuilt on every toggle.
  const selectionRef = useRef(selection);
  selectionRef.current = selection;

  useEffect(() => {
    const handlePointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        onCommit(selectionRef.current);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onCancel();
      } else if (e.key === "Enter") {
        e.preventDefault();
        onCommit(selectionRef.current);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onCommit, onCancel]);

  const toggle = (optionValue) => {
    const key = String(optionValue);
    setSelection((prev) => (
      prev.includes(key) ? prev.filter((v) => v !== key) : [...prev, key]
    ));
  };

  return (
    <div className={styles.multiSelect} ref={containerRef}>
      <div className={`${styles.field} ${styles.editor}`}>
        {selection.length === 0
          ? <span className={styles.placeholder}>Select options</span>
          : (
            <div className={styles.chips}>
              {selection.map((v) => {
                const option = options.find((o) => String(o.value) === v);
                return (
                  <span
                    key={v}
                    className={styles.chip}
                    style={option?.color ? { backgroundColor: option.color } : undefined}
                  >
                    {option ? option.label : v}
                  </span>
                );
              })}
            </div>
          )}
      </div>
      <div className={styles.optionList} role="listbox" aria-multiselectable="true">
        {options.map((option) => {
          const key = String(option.value);
          const checked = selection.includes(key);
          return (
            <label key={key} className={styles.optionRow}>
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => toggle(option.value)}
              />
              {option.color && <span className={styles.optionDot} style={{ background: option.color }} />}
              {option.label}
            </label>
          );
        })}
        {options.length === 0 && (
          <div className={styles.optionEmpty}>No options defined</div>
        )}
      </div>
    </div>
  );
}

/**
 * EditableField - an inline-editable value.
 *
 * Interaction model follows ClickUp / Resource Guru rather than a form:
 * - the value reads as a control at rest (hover reveals a field outline)
 * - one click enters edit mode; for select and date that click also opens the
 *   picker, so a change never costs two clicks
 * - blur or Enter commits, Escape reverts. There are no Save/Cancel buttons
 * - the display and the editor share identical box metrics, so entering edit
 *   mode never shifts the row; saving/error state is overlaid, not inserted
 *
 * Props:
 * - label: row label (ignored when bare)
 * - icon: Icon name shown before the label, so the property list can be
 *   scanned by glyph instead of by reading a column of grey words
 * - value: raw value used to seed the editor
 * - onSave(newValue): async callback, called on commit. Throwing keeps the
 *   field in edit mode with the draft intact so the user can correct it.
 * - type: "text" | "textarea" | "select" | "date" | "number" | "checkbox" |
 *         "multiselect"
 * - options: options for type="select" ({ value, label, color? }[])
 * - disabled: when true, renders read-only (no click-to-edit)
 * - formatDisplay(value): optional custom renderer for the read-only display
 * - placeholder: shown when the value is empty
 * - bare: skip the label/row wrapper, render just the control
 * - inline: shrink the control to its content instead of filling the row, for
 *   embedding in a sentence or a meta line
 * - flush: pull the control out by its own chip padding so the text aligns
 *   with whatever sits above it, while the hover chip still bleeds outward
 * - align: "left" | "right" — display alignment within the field (bare only)
 */
export default function EditableField({
  label,
  icon = null,
  value,
  onSave,
  type = "text",
  options = [],
  disabled = false,
  formatDisplay = null,
  placeholder = "—",
  bare = false,
  inline = false,
  flush = false,
  align = "left",
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  // Set while reverting so the blur that follows Escape doesn't also commit.
  const abortRef = useRef(false);
  const savedTimerRef = useRef(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => () => clearTimeout(savedTimerRef.current), []);

  useEffect(() => {
    if (!editing || !inputRef.current) return;
    const el = inputRef.current;
    el.focus();
    if (type === "date") {
      // Chromium/Edge: open the calendar on the same click that started editing
      try { el.showPicker?.(); } catch { /* unsupported or blocked */ }
    } else if (el.select) {
      el.select();
    }
  }, [editing, type]);

  const startEditing = () => {
    if (disabled || saving) return;
    abortRef.current = false;
    setDraft(value);
    setError(null);
    setEditing(true);
  };

  const revert = useCallback(() => {
    abortRef.current = true;
    setDraft(value);
    setError(null);
    setEditing(false);
  }, [value]);

  const commit = useCallback(async (newValue) => {
    if (abortRef.current) return;
    if (sameValue(newValue, value)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(newValue);
      setEditing(false);
      setSaved(true);
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaved(false), 1400);
    } catch (err) {
      // Stay in edit mode so the entered value isn't lost.
      setError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  }, [onSave, value]);

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      revert();
    } else if (e.key === "Enter") {
      if (type === "textarea" && !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      commit(draft);
    }
  };

  const handleBlur = () => {
    if (abortRef.current) {
      abortRef.current = false;
      return;
    }
    if (error) return; // let the user fix it rather than silently discarding
    commit(draft);
  };

  const renderDisplay = () => {
    if (formatDisplay) {
      const rendered = formatDisplay(value);
      if (rendered) return rendered;
    } else if (type === "select") {
      const match = options.find((o) => String(o.value) === String(value));
      if (match) return match.label;
    } else if (type === "multiselect") {
      const selected = Array.isArray(value) ? value : [];
      if (selected.length > 0) {
        return (
          <span className={styles.chips}>
            {selected.map((v) => {
              const option = options.find((o) => String(o.value) === String(v));
              return (
                <span
                  key={String(v)}
                  className={styles.chip}
                  style={option?.color ? { backgroundColor: option.color } : undefined}
                >
                  {option ? option.label : String(v)}
                </span>
              );
            })}
          </span>
        );
      }
    } else if (value !== null && value !== undefined && value !== "") {
      return value;
    }
    return <span className={styles.placeholder}>{placeholder}</span>;
  };

  const fieldClass = [
    styles.field,
    disabled ? styles.readOnly : styles.interactive,
    align === "right" ? styles.alignRight : "",
    type === "textarea" ? styles.multiline : "",
    saved ? styles.justSaved : "",
  ].filter(Boolean).join(" ");

  const wrapperClass = [
    styles.control,
    inline ? styles.inlineControl : "",
    flush ? styles.flushControl : "",
  ].filter(Boolean).join(" ");

  let control;
  if (type === "checkbox") {
    // A tick box is already a control at rest — there's nothing to reveal, and
    // toggling it *is* the commit, so it skips the click-to-edit round trip.
    control = (
      <span className={`${fieldClass} ${styles.checkboxField}`}>
        <input
          type="checkbox"
          checked={value === true}
          disabled={disabled || saving}
          onChange={(e) => commit(e.target.checked)}
        />
        <span className={styles.checkboxCaption}>{value === true ? "Yes" : "No"}</span>
      </span>
    );
  } else if (editing && type === "multiselect") {
    control = (
      <MultiSelectEditor
        options={options}
        value={draft}
        disabled={saving}
        onCommit={commit}
        onCancel={revert}
      />
    );
  } else if (editing && type === "select") {
    control = (
      <CustomDropdown
        fullWidth
        compact
        autoOpen
        value={draft}
        options={options}
        disabled={saving}
        onChange={(val) => { setDraft(val); commit(val); }}
        onClose={() => setEditing(false)}
      />
    );
  } else if (editing && type === "textarea") {
    control = (
      <textarea
        ref={inputRef}
        className={`${styles.field} ${styles.editor} ${styles.multiline}`}
        value={draft || ""}
        disabled={saving}
        rows={3}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    );
  } else if (editing) {
    control = (
      <input
        ref={inputRef}
        type={type === "date" ? "date" : type === "number" ? "number" : "text"}
        className={`${styles.field} ${styles.editor}`}
        value={draft ?? ""}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
      />
    );
  } else if (disabled) {
    control = <span className={fieldClass}>{renderDisplay()}</span>;
  } else {
    // A div rather than a button: displays can contain links (mailto:) and
    // nesting an <a> inside a <button> is invalid HTML.
    control = (
      <div
        className={fieldClass}
        role="button"
        tabIndex={0}
        onClick={startEditing}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            startEditing();
          }
        }}
      >
        {renderDisplay()}
      </div>
    );
  }

  const wrapper = (
    <span className={wrapperClass}>
      {control}
      {saving && <span className={styles.spinner} aria-hidden="true" />}
      {error && <span className={styles.error} role="alert">{error}</span>}
    </span>
  );

  if (bare) return wrapper;

  return (
    <div className={styles.row}>
      <span className={styles.rowLabel}>
        {icon && <Icon name={icon} size="0.95rem" className={styles.rowIcon} />}
        {label}
      </span>
      {wrapper}
    </div>
  );
}
